/** @format */
/* eslint-disable */ 
import Day from './day';
import Display from './display';
import dayjs from 'dayjs';
import { $csI18n } from '@/js/modules/csI18n';

const $_keypressPrivate = {
    /**
     * @public
     * @description
     * Takes the incoming config and updates the active date that our keypress is focused on
     * 
     * @param {Object} config adjustment (add/subtract), amount (integer) and unit (day)
     * @param {Object} instance the current datepicker instance
     */
    setKeyFocusDate(config, instance) {
		let lastDate = undefined; 
		if (instance.keyFocusDate != undefined) {
			lastDate = instance.keyFocusDate;
		} else if (instance.selectedDates.length === 0) {
			lastDate = instance.today;
			if (config.amount) config.amount = 0;
		} else if (instance.selectedDates.last().date.jsDateRef) {
			lastDate = instance.selectedDates.last();
		} else {
			throw new Error($csI18n.t('datepicker.unableEstablishDate')); 
        }
        
		let keyFocusDate;
		if (typeof config === 'function') {
			keyFocusDate = config(lastDate);
		} else {
			keyFocusDate = Day.getDateObjectFromDateString(dayjs(lastDate.date.jsDateRef)[config.adjustment](config.amount, config.unit), instance);
		}
		if (keyFocusDate.date.isSelectable) {
			instance.keyFocusDate = keyFocusDate;
			Display.updateCalendarViewFromDate(keyFocusDate, instance);
		}
    },

    /**
     * @public
     * @description
     * Handle the shift and tab key event when cycling backwards.
     * In range mode we are also moving between the start and end dates and
     * then either back to the 'Next Month' button
     * 
     * @param {Object} instance the current datepicker instance
     */
    onTabShiftPressed(instance) {
        if (instance.selectedTabIndex == undefined) {
            instance.selectedTabIndex = (instance.selectedDates.length - 1);
        } else {
            instance.selectedTabIndex--;
        }

        if (!instance.selectedDates[instance.selectedTabIndex] || instance.selectedDates.length === 0) {
            $_keypressPrivate.clearTabData(instance);
            const previousButton = document.getElementById('lc-next-month-button');
            if (previousButton) previousButton.focus();
        } else {
            $_keypressPrivate.fireRangeTabChange(instance);
        }
    },

    /**
     * @public
     * @description
     * Handle the shift and tab key event when cycling backwards.
     * In range mode we are also moving between the start and end dates and
     * then forward to the custom range buttons.
     * 
     * @param {Object} instance the current datepicker instance
     */
    onTabPressed(instance) {
        if (instance.selectedTabIndex == undefined) {
            instance.selectedTabIndex = 0;
        } else {
            instance.selectedTabIndex++;
        }

        if (!instance.selectedDates[instance.selectedTabIndex] || instance.selectedDates.length === 0) {
            $_keypressPrivate.clearTabData(instance);
            const rangeButtons = document.getElementsByClassName('lc-range-button');
            if (rangeButtons[0]) {
                rangeButtons[0].focus();
            }
        } else {
            $_keypressPrivate.fireRangeTabChange(instance);
        }
    },

    /**
     * @public
     * @description
     * Clears any data that we have assigned during use of the tab key
     * 
     * @param {Object} instance the current datepicker instance
     */
    clearTabData(instance) {
        instance.resetKeyPressData();
    },

    /**
     * @public
     * @description
     * Updates instance tab properties and then changes the active selected date
     * that we are focusing on.
     * 
     * @param {Object} instance the current datepicker instance
     */
    fireRangeTabChange(instance) {
        if (instance.selectedDates[instance.selectedTabIndex]) {
            instance.tabFocusDate = instance.selectedDates[instance.selectedTabIndex];
            instance.keyFocusDate = instance.selectedDates[instance.selectedTabIndex];
        }
        instance.updateActiveSelectedDate(instance.selectedDates[instance.selectedTabIndex], instance.selectedTabIndex);
    }
}

export const $_keypressPublic = {
    /**
     * @public
     * @description
     * Sets the focus date config based on pressing the up key
     * 
     * @param {Object} instance the current datepicker instance
     */
    upKey(instance) {
        $_keypressPrivate.setKeyFocusDate(({ date }) => {
            let targetDate = undefined, isSelectable = false, maxIterations = 0;
            // perform a search for the next applicable selectable date a reasonable amount
            // of times and then throw an error if nothing matched
            while (!isSelectable && maxIterations <= 50) {
                const increment = ((maxIterations * 7) + 7)
                targetDate = Day.getDateObjectFromDateString(dayjs(date.jsDateRef).subtract((increment), 'day'), instance);
                if (targetDate.date) {
                    isSelectable = targetDate.date.isSelectable;
                }
                maxIterations++;
            }
            if (maxIterations == 50) {
                throw new Error($csI18n.t('datepicker.unableEstablishDate'));
            }
            if (targetDate != undefined) {
                return targetDate;
            }
		}, instance);
	},

    /**
     * @public
     * @description
     * Sets the focus date config based on pressing the down key
     * 
     * @param {Object} instance the current datepicker instance
     */
	downKey(instance) {
		$_keypressPrivate.setKeyFocusDate(({ date }) => {
            let targetDate = undefined, isSelectable = false, maxIterations = 0;
            // perform a search for the next applicable selectable date a reasonable amount
            // of times and then throw an error if nothing matched
            while (!isSelectable && maxIterations <= 50) {
                const increment = ((maxIterations * 7) + 7)
                targetDate = Day.getDateObjectFromDateString(dayjs(date.jsDateRef).add((increment), 'day'), instance);
                if (targetDate.date) {
                    isSelectable = targetDate.date.isSelectable;
                }
                maxIterations++;
            }
            if (maxIterations == 50) {
                throw new Error($csI18n.t('datepicker.unableEstablishDate'));
            }
            if (targetDate != undefined) {
                return targetDate;
            }
		}, instance);
	},

    /**
     * @public
     * @description
     * Sets the focus date config based on pressing the left key
     * 
     * @param {Object} instance the current datepicker instance
     */
	keyleft(instance) {
		$_keypressPrivate.setKeyFocusDate(({ date }) => {
            let targetDate = undefined, isSelectable = false, maxIterations = 0;
            // perform a search for the next applicable selectable date a reasonable amount
            // of times and then throw an error if nothing matched
            while (!isSelectable && maxIterations <= 50) {
                const increment = (maxIterations + 1)
                targetDate = Day.getDateObjectFromDateString(dayjs(date.jsDateRef).subtract((increment), 'day'), instance);
 
                if (targetDate.date) {
                    isSelectable = targetDate.date.isSelectable;
                }
                maxIterations++;
            }
            if (maxIterations == 50) {
                throw new Error($csI18n.t('datepicker.unableEstablishDate'));
            }
            if (targetDate != undefined) {
                return targetDate;
            }
		}, instance);
	},

    /**
     * @public
     * @description
     * Sets the focus date config based on pressing the right key
     * 
     * @param {Object} instance the current datepicker instance
     */
	keyright(instance) {
		$_keypressPrivate.setKeyFocusDate(({ date }) => {
			let targetDate = undefined, isSelectable = false, maxIterations = 0;
            // perform a search for the next applicable selectable date a reasonable amount
            // of times and then throw an error if nothing matched
            while (!isSelectable && maxIterations <= 50) {
                const increment = (maxIterations + 1)
                targetDate = Day.getDateObjectFromDateString(dayjs(date.jsDateRef).add((increment), 'day'), instance);
                if (targetDate.date) {
                    isSelectable = targetDate.date.isSelectable;
                }
                maxIterations++;
            }
            if (maxIterations == 50) {
                throw new Error($csI18n.t('datepicker.unableEstablishDate'));
            }
            if (targetDate != undefined) {
                return targetDate;
            }
		}, instance);
	},

    /**
     * @public
     * @description
     * Sets the focus date config based on pressing the up key
     * 
     * @param {Object} instance the current datepicker instance
     * @param {Object} event the tab key press event
     */
	onTabDown(instance, event) {
		if (instance.calendarMode === 'range') {
            if (event.shiftKey) {
                $_keypressPrivate.onTabShiftPressed(instance);
            } else {
                $_keypressPrivate.onTabPressed(instance);
            }
            event.preventDefault();
		}
        
		return;
    }
};

export {$_keypressPublic as default};
